import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { CreateTestRequst, fetchUserDataRequest } from '../../actions/dashboardActions';
import InputTranslater from '../common/InputTranslater';
import { ValidateTestCreateForm } from './ValidateTestCreateForm';
import { clearMessage } from '../../actions/userActions'
import { FiTrash2, FiPlus, FiX, FiCheck, FiRadio } from 'react-icons/fi';
import './CreateTest.css';
import RichTextEditor from '../common/RichTextEditor';
import OpenAIQuestionGenerator from '../common/OpenAIQuestionGenerator';

function CreateTest() {
    // Load saved data from sessionStorage if available
    const savedData = JSON.parse(sessionStorage.getItem('testFormData')) || {};
    const [formData, setFormData] = useState({
        testName: savedData.testName || '',
        category: savedData.category || '',
        minutes: savedData.minutes || '',
        outOfMarks: savedData.outOfMarks || '',
        language: savedData.language || 'en',
        customCategory: savedData.customCategory || '',
        testSeries: savedData.testSeries || '',
        defaultQuestionType: savedData.defaultQuestionType || 'single',
        markDistribution: savedData.markDistribution || 'total', // 'total' or 'question'
        passingPercentage: savedData.passingPercentage || 60, // Default to 50%

    });

    const [errors, setErrors] = useState([]);
    const [notification, setNotification] = useState({ show: false, message: '', type: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

    const testSeriesOptions = [
        "SSC",
        "Banking",
        "RRB",
        "MPSC",
        "Police",
        "Coding",
        "Placement",
        "JEE",
        "NA"
    ];

    const dispatch = useDispatch();
    const [questions, setQuestions] = useState(savedData.questions || [{
        id: Date.now(),
        question: '',
        options: ['', '', '', ''],
        correctOption: '',
        correctOptions: [],
        type: savedData.defaultQuestionType || 'single',
        explanation: '',
        showExplanation: false,
        marks: 1, // Default marks per question
    }]);
    const teacherProfileData = useSelector(state => state.dashboard.userData);
    const testCreatedSuccess = useSelector(state => state.dashboard.testCreatedSuccess);
    const testCreatedFailure = useSelector(state => state.dashboard.testCreatedFailure);

    useEffect(() => {
        if (!teacherProfileData) {
            dispatch(fetchUserDataRequest());
        }
    }, [dispatch, teacherProfileData]);

    // Save form data to sessionStorage
    useEffect(() => {
        const dataToSave = {
            ...formData,
            questions
        };
        sessionStorage.setItem('testFormData', JSON.stringify(dataToSave));
    }, [formData, questions]);

    const handleAddQuestion = () => {
        setQuestions([...questions, {
            id: Date.now(),
            question: '',
            options: ['', '', '', ''],
            correctOption: '',
            correctOptions: [],
            type: formData.defaultQuestionType,
            explanation: '',
            showExplanation: false,
            marks: 1, // Default marks for new question
        }]);
    };

    const showNotification = (message, type) => {
        setNotification({ show: true, message, type });
        setTimeout(() => {
            setNotification({ show: false, message: '', type: '' });
        }, 3000);
    };

    useEffect(() => {
        if (testCreatedSuccess) {
            setIsSubmitting(false);
            clearForm();
            showNotification("Test Created Successfully!", "success");
            setTimeout(() => {
                dispatch(clearMessage());
            }, 2000);
        }
        if (testCreatedFailure) {
            setIsSubmitting(false);
            showNotification("Test Creation Failed. Please try again.", "error");
            setTimeout(() => {
                dispatch(clearMessage());
            }, 2000);
        }
    }, [testCreatedSuccess, testCreatedFailure, dispatch]);

    const clearForm = () => {
        setFormData({
            testName: '',
            category: '',
            minutes: '',
            outOfMarks: '',
            language: 'en',
            customCategory: '',
            testSeries: '',
            defaultQuestionType: 'single',
            markDistribution: 'total',
            passingPercentage: 60, // Reset to default
        });
        setQuestions([{
            id: Date.now(),
            question: '',
            options: ['', '', '', ''],
            correctOption: '',
            correctOptions: [],
            type: 'single',
            explanation: '',
            showExplanation: false,
            marks: 1
        }]);
        setErrors([]);
        sessionStorage.removeItem('testFormData');
    };

    const handleReset = () => {
        if (window.confirm("Are you sure you want to reset the form? All your changes will be lost.")) {
            clearForm();
            showNotification("Form has been reset", "info");
        }
    };

    const prepareTestData = () => {
        const questionsData = questions.map((q, index) => ({
            questionNumber: index + 1,
            questionText: q.question,
            options: q.options,
            type: q.type,
            explanation: q.explanation || null,
            marks: formData.markDistribution === 'question' ? q.marks : null
        }));

        const correctAnswers = questions.map(q => {
            if (q.type === 'single') {
                return q.correctOption - 1;
            } else {
                return q.correctOptions.map(opt => opt - 1);
            }
        });

        return {
            teacherId: teacherProfileData?._id || "s",
            teacherName: teacherProfileData?.name || "d",
            testName: formData.testName,
            totalMinutes: Number(formData.minutes),
            totalMarks: formData.markDistribution === 'total' ? Number(formData.outOfMarks) : questions.reduce((sum, q) => sum + (q.marks || 0), 0),
            testSeries: formData.testSeries === "NA" || formData.testSeries === "" ? null : formData.testSeries,
            category: formData.category === 'other' ? formData.customCategory : formData.category,
            questions: questionsData,
            correctAnswers: correctAnswers,
            markDistribution: formData.markDistribution,
            passingPercentage: Number(formData.passingPercentage),

        };
    };

    const handleSubmit = e => {
        e.preventDefault();
        setIsSubmitting(true);

        const validationErrors = ValidateTestCreateForm(
            formData,
            questions,
        );
        if (validationErrors.length > 0) {
            setErrors(validationErrors);
            setIsSubmitting(false);
            const firstErrorField = validationErrors[0].field;
            const firstErrorElement = document.querySelector(`[name="${firstErrorField}"]`);
            if (firstErrorElement) {
                firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        const testData = prepareTestData();
        dispatch(CreateTestRequst(testData));
    };

    const confirmDeleteQuestion = (questionId) => {
        setShowDeleteConfirm(questionId);
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(null);
    };

    const deleteQuestion = (questionId) => {
        const updatedQuestions = questions.filter(q => q.id !== questionId);
        setQuestions(updatedQuestions);
        setShowDeleteConfirm(null);
        showNotification("Question deleted", "info");
    };

    const getError = (field) => {
        const error = errors.find(error =>
            error.field.toLowerCase() === field.toLowerCase()
        );
        return error ? error.message : null;
    };

    const categories = [
        "History", "Computer", "Economics", "Geography", "Mathematics",
        "Current affairs", "General knowledge", "Logical reasoning",
        "Quantitative Aptitude", "other"
    ];

    const handleCorrectAnswerChange = (questionId, optionIndex, isChecked) => {
        setQuestions(prev => prev.map(q => {
            if (q.id !== questionId) return q;

            const optionNumber = optionIndex + 1;

            if (q.type === 'single') {
                return { ...q, correctOption: isChecked ? optionNumber : '' };
            } else {
                let newCorrectOptions = [...(q.correctOptions || [])];
                if (isChecked) {
                    if (!newCorrectOptions.includes(optionNumber)) {
                        newCorrectOptions.push(optionNumber);
                    }
                } else {
                    newCorrectOptions = newCorrectOptions.filter(opt => opt !== optionNumber);
                }
                return { ...q, correctOptions: newCorrectOptions };
            }
        }));
    };

    const handleRemoveOption = (questionId, optionIndex) => {
        setQuestions(prev => prev.map(q => {
            if (q.id !== questionId) return q;

            const newOptions = [...q.options];
            newOptions.splice(optionIndex, 1);

            let newCorrectOption = q.correctOption;
            let newCorrectOptions = [...(q.correctOptions || [])];

            if (q.type === 'single' && q.correctOption > optionIndex + 1) {
                newCorrectOption = q.correctOption - 1;
            } else if (q.type === 'multiple') {
                newCorrectOptions = (q.correctOptions || [])
                    .filter(opt => opt !== optionIndex + 1)
                    .map(opt => opt > optionIndex + 1 ? opt - 1 : opt);
            }

            return {
                ...q,
                options: newOptions,
                correctOption: newCorrectOption,
                correctOptions: newCorrectOptions
            };
        }));
    };

    const handleAddOption = (questionId) => {
        setQuestions(prev => prev.map(q =>
            q.id === questionId
                ? { ...q, options: [...q.options, ''] }
                : q
        ));
    };

    const handleQuestionTypeChange = (questionId, type) => {
        setQuestions(prev => prev.map(q =>
            q.id === questionId
                ? { ...q, type, correctOption: '', correctOptions: [] }
                : q
        ));
    };

    const toggleDefaultQuestionType = () => {
        const newType = formData.defaultQuestionType === 'single' ? 'multiple' : 'single';
        setFormData(prev => ({
            ...prev,
            defaultQuestionType: newType
        }));
    };

    const toggleMarkDistribution = () => {
        const newDistribution = formData.markDistribution === 'total' ? 'question' : 'total';
        setFormData(prev => ({
            ...prev,
            markDistribution: newDistribution
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleQuestionMarksChange = (questionId, value) => {
        setQuestions(prev => prev.map(q =>
            q.id === questionId
                ? { ...q, marks: value === '' ? '' : Math.max(1, parseInt(value) || 1) }
                : q
        ));
    };

    const toggleExplanation = (questionId) => {
        setQuestions(prev => prev.map(q =>
            q.id === questionId
                ? { ...q, showExplanation: !q.showExplanation }
                : q
        ));
    };

    return (
        <div className="container-fluid create-test-container">
            {/* Notification */}
            {notification.show && (
                <div className={`alert alert-${notification.type === 'success' ? 'success' : notification.type === 'error' ? 'danger' : 'info'} alert-dismissible fade show notification-alert`}
                    role="alert">
                    {notification.message}
                    <button type="button" className="btn-close" onClick={() => setNotification({ show: false, message: '', type: '' })}></button>
                </div>
            )}

            <div className="row justify-content-center">
                <div className="col-12 col-lg-10">
                    <div className="card card-shadow">
                        <div className="card-body p-4">
                            <form onSubmit={handleSubmit}>
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center form-header">
                                    <h2 className="mb-3 mb-md-0">Create Test</h2>
                                    <div className="d-flex flex-column flex-sm-row gap-2 header-actions">
                                        <select
                                            className="form-select"
                                            style={{ width: 'auto' }}
                                            value={formData.language}
                                            onChange={handleInputChange}
                                            name="language"
                                        >
                                            <option value="en">English</option>
                                            <option value="mr">Marathi</option>
                                            <option value="hi">Hindi</option>
                                            <option value="bn">Bengali</option>
                                            <option value="te">Telugu</option>
                                            <option value="ta">Tamil</option>
                                            <option value="gu">Gujarati</option>
                                            <option value="kn">Kannada</option>
                                            <option value="ml">Malayalam</option>
                                            <option value="ur">Urdu</option>
                                        </select>
                                        <button
                                            type="button"
                                            className="btn btn-warning"
                                            onClick={handleReset}
                                        >
                                            Reset Form
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-success"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Creating...
                                                </>
                                            ) : 'Create Test'}
                                        </button>
                                    </div>
                                </div>

                                {/* Test Details */}
                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Test Name</label>
                                        <InputTranslater
                                            value={formData.testName}
                                            onChangeText={(text) => setFormData(prev => ({ ...prev, testName: text }))}
                                            type="text"
                                            className={`form-control ${getError('testName') ? 'is-invalid' : ''}`}
                                            name="testName"
                                            toLanguage={formData.language}
                                        />
                                        {getError('testName') && (
                                            <div className="invalid-feedback" style={{ display: 'block' }}>
                                                {getError('testName')}
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Category</label>
                                        <select
                                            className={`form-select ${getError('category') ? 'is-invalid' : ''}`}
                                            name="category"
                                            value={formData.category}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Select Category</option>
                                            {categories.map((cat, index) => (
                                                <option key={index} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                        {getError('category') && <div className="invalid-feedback">{getError('category')}</div>}
                                    </div>
                                </div>


                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Test Series</label>
                                        <select
                                            className="form-select"
                                            name="testSeries"
                                            value={formData.testSeries}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Select Test Series</option>
                                            {testSeriesOptions.map((option, index) => (
                                                <option key={index} value={option}>{option}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Duration (minutes)</label>
                                        <input
                                            type="number"
                                            className={`form-control ${getError('minutes') ? 'is-invalid' : ''}`}
                                            name="minutes"
                                            value={formData.minutes}
                                            onChange={handleInputChange}
                                            min="1"
                                        />
                                        {getError('minutes') && <div className="invalid-feedback">{getError('minutes')}</div>}
                                    </div>
                                </div>

                                {formData.category === "other" && (
                                    <div className="mb-4">
                                        <label className="form-label fw-bold">Custom Category</label>
                                        <input
                                            type="text"
                                            className={`form-control ${getError('customCategory') ? 'is-invalid' : ''}`}
                                            name="customCategory"
                                            value={formData.customCategory}
                                            onChange={handleInputChange}
                                        />
                                        {getError('customCategory') && <div className="invalid-feedback">{getError('customCategory')}</div>}
                                    </div>
                                )}

                                <div className="row g-3 mb-4">

                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <label className="form-label fw-bold m-0">
                                                {formData.markDistribution === 'total' ? 'Total Marks' : 'Marks per Question'}
                                            </label>
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="markDistributionSwitch"
                                                    checked={formData.markDistribution === 'question'}
                                                    onChange={toggleMarkDistribution}
                                                />
                                                <label className="form-check-label small" htmlFor="markDistributionSwitch">
                                                    {formData.markDistribution === 'total' ? 'Use question-level marks' : 'Use total marks'}
                                                </label>
                                            </div>
                                        </div>

                                        {formData.markDistribution === 'total' ? (
                                            <input
                                                type="number"
                                                className={`form-control ${getError('outOfMarks') ? 'is-invalid' : ''}`}
                                                name="outOfMarks"
                                                value={formData.outOfMarks}
                                                onChange={handleInputChange}
                                                min="1"
                                            />
                                        ) : (
                                            <div className="d-flex align-items-center">
                                                <span className="me-2">Total:</span>
                                                <strong>{questions.reduce((sum, q) => sum + (q.marks || 0), 0)}</strong>
                                                <span className="ms-2">marks ({questions.length} questions)</span>
                                            </div>
                                        )}
                                        {getError('outOfMarks') && <div className="invalid-feedback">{getError('outOfMarks')}</div>}
                                    </div>



                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Passing Percentage</label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="passingPercentage"
                                                value={formData.passingPercentage}
                                                onChange={handleInputChange}
                                                min="0"
                                                max="100"
                                            />
                                            <span className="input-group-text">%</span>
                                        </div>
                                        {getError('passingPercentage') && (
                                            <div className="invalid-feedback d-block">
                                                {getError('passingPercentage')}
                                            </div>
                                        )}
                                        <div className="form-text">Minimum percentage required to pass the test</div>
                                    </div>
                                </div>


                                <div className="mb-4">
                                    {/* Questions Section Header */}
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2 gap-md-0">
                                        <h4 className="mb-0">Questions</h4>
                                        <OpenAIQuestionGenerator
                                            onQuestionsGenerated={(generatedQuestions, topic) => {
                                                const isQuestionEmpty = (q) => (
                                                    q.question.trim() === '' &&
                                                    q.options.every(opt => opt.trim() === '') &&
                                                    (q.explanation || '').trim() === ''
                                                );

                                                // Update test name with the generated topic
                                                setFormData(prev => ({
                                                    ...prev,
                                                    testName: topic
                                                }));

                                                // Case 1: First question is empty - replace it
                                                if (isQuestionEmpty(questions[0])) {
                                                    setQuestions([...generatedQuestions, ...questions.slice(1)]);
                                                    return;
                                                }

                                                // Default case: append new questions
                                                setQuestions([...questions, ...generatedQuestions]);
                                            }}
                                            defaultTopic={formData.testName || formData.category}
                                            defaultQuestionType={formData.defaultQuestionType}
                                        />
                                        <div className="type-toggle-container">
                                            <span className="type-label">Default type:</span>
                                            <div className="type-toggle">
                                                <span className={formData.defaultQuestionType === 'single' ? 'single-type' : 'multiple-type'}>
                                                    {formData.defaultQuestionType === 'single' ? 'Single' : 'Multiple'}
                                                </span>
                                                <div className="form-check form-switch m-0 d-flex align-items-center">
                                                    <input
                                                        className="form-check-input type-toggle-switch"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="defaultTypeSwitch"
                                                        checked={formData.defaultQuestionType === 'multiple'}
                                                        onChange={toggleDefaultQuestionType}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Questions List */}
                                    {questions.map((question, qIndex) => (
                                        <div key={question.id} className="card mb-4 p-3 question-card">
                                            {/* Question Header */}
                                            {/* Question Header */}
                                            <div className="d-flex justify-content-between align-items-center mb-2 question-header">
                                                <h5 className="mb-0">Question {qIndex + 1}</h5>
                                                <div className="d-flex flex-column align-items-end">
                                                    <div className="d-flex align-items-center gap-2">
                                                        {formData.markDistribution === 'question' && (
                                                            <div className="d-flex flex-column">
                                                                <div className="d-flex align-items-center marks-input-container">
                                                                    <label className="form-label small m-0 me-2">Marks:</label>
                                                                    <input
                                                                        type="number"
                                                                        className={`form-control form-control-sm marks-input ${getError(`question${qIndex}Marks`) ? 'is-invalid' : ''}`}
                                                                        style={{ width: '60px' }}
                                                                        value={question.marks}
                                                                        onChange={(e) => handleQuestionMarksChange(question.id, e.target.value)}
                                                                        min="1"
                                                                    />
                                                                </div>

                                                            </div>
                                                        )}
                                                        {questions.length > 1 && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger btn-sm d-flex align-items-center justify-content-center"
                                                                style={{ width: '2rem', height: '2rem', padding: 0 }}
                                                                onClick={() => confirmDeleteQuestion(question.id)}
                                                                title="Delete question"
                                                            >
                                                                <FiTrash2 size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Delete Confirmation Modal */}
                                            {showDeleteConfirm === question.id && (
                                                <div
                                                    className="modal fade show"
                                                    tabIndex="-1"
                                                    style={{
                                                        display: 'block',
                                                        background: 'rgba(0,0,0,0.4)',
                                                        position: 'fixed',
                                                        top: 0, left: 0, right: 0, bottom: 0,
                                                        zIndex: 2000
                                                    }}
                                                    aria-modal="true"
                                                    role="dialog"
                                                >
                                                    <div className="modal-dialog modal-dialog-centered">
                                                        <div className="modal-content p-3">
                                                            <div className="modal-header border-0">
                                                                <h5 className="modal-title">Confirm Delete</h5>
                                                                <button type="button" className="btn-close" onClick={cancelDelete}></button>
                                                            </div>
                                                            <div className="modal-body">
                                                                Are you sure you want to delete this question?
                                                            </div>
                                                            <div className="modal-footer border-0">
                                                                <button type="button" className="btn btn-secondary" onClick={cancelDelete}>Cancel</button>
                                                                <button type="button" className="btn btn-danger" onClick={() => deleteQuestion(question.id)}>Delete</button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Question Text */}
                                            <div className="mb-3">
                                                <label className="form-label">Question Text</label>
                                                <RichTextEditor
                                                    defaultValue={question.question}
                                                    onChange={(text) => {
                                                        setQuestions(prev => prev.map(q =>
                                                            q.id === question.id ? { ...q, question: text } : q
                                                        ));
                                                    }}
                                                    placeholder="Please enter your question here..."
                                                    className={`form-cont ${getError(`question${qIndex}`) ? 'is-invalid' : ''}`}
                                                    language={formData.language}
                                                />
                                                {getError(`question${qIndex}`) && (
                                                    <div className="invalid-feedback">{getError(`question${qIndex}`)}</div>
                                                )}
                                            </div>

                                            {/* Options */}
                                            <div className="mb-3">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <label className="form-label">Options</label>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="d-flex align-items-center">
                                                            {question.type === 'single' ? (
                                                                <>
                                                                    <FiRadio className="me-1 single-type" />
                                                                    <span className="me-2">Single</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FiCheck className="me-1 multiple-type" />
                                                                    <span className="me-2">Multiple</span>
                                                                </>
                                                            )}
                                                            <div className="form-check form-switch m-0">
                                                                <input
                                                                    className="form-check-input type-toggle-switch-small"
                                                                    type="checkbox"
                                                                    role="switch"
                                                                    checked={question.type === 'multiple'}
                                                                    onChange={() => handleQuestionTypeChange(
                                                                        question.id,
                                                                        question.type === 'single' ? 'multiple' : 'single'
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => handleAddOption(question.id)}
                                                        >
                                                            <FiPlus className="me-1" /> Add Option
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="row g-2">
                                                    {question.options.map((option, optIndex) => (
                                                        <div className="col-12" key={optIndex}>
                                                            <div className="input-group mb-2 option-input-group">
                                                                <span className="input-group-text">
                                                                    {question.type === 'single' ? (
                                                                        <input
                                                                            type="radio"
                                                                            name={`correct-${question.id}`}
                                                                            checked={question.correctOption === optIndex + 1}
                                                                            onChange={(e) => handleCorrectAnswerChange(question.id, optIndex, e.target.checked)}
                                                                            className="form-check-input mt-0"
                                                                        />
                                                                    ) : (
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={question.correctOptions?.includes(optIndex + 1)}
                                                                            onChange={(e) => handleCorrectAnswerChange(question.id, optIndex, e.target.checked)}
                                                                            className="form-check-input mt-0"
                                                                        />
                                                                    )}
                                                                </span>
                                                                <div className="flex-grow-1">
                                                                    <InputTranslater
                                                                        type="text"
                                                                        className={`form-control no-radius ${getError(`option${qIndex}-${optIndex}`) ? 'is-invalid' : ''}`}
                                                                        placeholder={`Option ${optIndex + 1}`}
                                                                        value={option}
                                                                        onChangeText={(text) => {
                                                                            setQuestions(prev => prev.map(q => {
                                                                                if (q.id !== question.id) return q;
                                                                                const newOptions = [...q.options];
                                                                                newOptions[optIndex] = text;
                                                                                return { ...q, options: newOptions };
                                                                            }));
                                                                        }}
                                                                        toLanguage={formData.language}
                                                                        name={`option-${question.id}-${optIndex}`}
                                                                    />
                                                                </div>
                                                                {question.options.length > 2 && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger"
                                                                        onClick={() => handleRemoveOption(question.id, optIndex)}
                                                                    >
                                                                        <FiX />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {getError(`option${qIndex}-${optIndex}`) && (
                                                                <div className="invalid-feedback d-block">{getError(`option${qIndex}-${optIndex}`)}</div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Correct Answer Feedback */}
                                            {getError(`correctOption${qIndex}`) && (
                                                <div className="alert alert-danger p-2">
                                                    {getError(`correctOption${qIndex}`)}
                                                </div>
                                            )}

                                            {/* Explanation Section */}
                                            <div className="mb-3">
                                                <div className="d-flex align-items-center mb-2">
                                                    <button
                                                        type="button"
                                                        className={`btn btn-sm ${question.showExplanation ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                                                        onClick={() => toggleExplanation(question.id)}
                                                        style={{ marginRight: '10px' }}
                                                    >
                                                        {question.showExplanation ? (
                                                            <>
                                                                <FiX className="me-1" /> Remove Explanation
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FiPlus className="me-1" /> Add Explanation
                                                            </>
                                                        )}
                                                    </button>
                                                    {question.showExplanation && (
                                                        <span className="text-muted small">
                                                            (Explanation will be shown to students after they answer)
                                                        </span>
                                                    )}
                                                </div>

                                                {question.showExplanation && (
                                                    <div className="explanation-container">
                                                        <label className="form-label">Explanation for Correct Answer</label>
                                                        <InputTranslater
                                                            as="textarea"
                                                            value={question.explanation}
                                                            onChangeText={(text) => {
                                                                setQuestions(prev => prev.map(q =>
                                                                    q.id === question.id ? { ...q, explanation: text } : q
                                                                ));
                                                            }}
                                                            className={`form-control ${getError(`explanation${qIndex}`) ? 'is-invalid' : ''}`}
                                                            placeholder="Explain why this answer is correct (optional)..."
                                                            toLanguage={formData.language}
                                                            name={`explanation-${question.id}`}
                                                        />
                                                        <div className="form-text">This will help students understand why the correct answer is right.</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="d-grid gap-2 d-md-flex justify-content-md-end form-actions">
                                      <OpenAIQuestionGenerator
                                            onQuestionsGenerated={(generatedQuestions, topic) => {
                                                const isQuestionEmpty = (q) => (
                                                    q.question.trim() === '' &&
                                                    q.options.every(opt => opt.trim() === '') &&
                                                    (q.explanation || '').trim() === ''
                                                );

                                                // Update test name with the generated topic
                                                setFormData(prev => ({
                                                    ...prev,
                                                    testName: topic
                                                }));

                                                // Case 1: First question is empty - replace it
                                                if (isQuestionEmpty(questions[0])) {
                                                    setQuestions([...generatedQuestions, ...questions.slice(1)]);
                                                    return;
                                                }

                                                // Default case: append new questions
                                                setQuestions([...questions, ...generatedQuestions]);
                                            }}
                                            defaultTopic={formData.testName || formData.category}
                                            defaultQuestionType={formData.defaultQuestionType}
                                        />
                                    <button
                                        type="button"
                                        className="btn btn-primary me-md-2"
                                        onClick={handleAddQuestion}
                                    >
                                        <FiPlus className="me-1" /> Add Question
                                    </button>                    
                                    <button
                                        type="button"
                                        className="btn btn-secondary me-md-2"
                                        onClick={handleReset}
                                    >
                                        Reset Form
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-success px-4"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Creating...
                                            </>
                                        ) : 'Create Test'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CreateTest;